import {applySnapshot, cast, flow, Instance, types} from "mobx-state-tree"
import Screens from "../constants/Screens";
import AuthStore, {AuthDefaultStore} from "./AuthStore";
import OnboardingStore, {OnboardingDefaultStore} from "./OnboardingStore";
import LandingPageStore, {LandingPageDefaultStore} from "./LandingPageStore";
import GroupStore, {GroupDefaultStore} from "./GroupStore";
import DeeplinkStore, {DeeplinkDefaultStore} from "./DeeplinkStore";
import agent, {getCSBURL, getPhoenixURL} from "../agent";
import {formatI18nKeys} from "../utils/I18n";
import CountryCodeList from "../utils/CountryCodeList";
import {Base64} from 'js-base64';
import {createImageStoreWithRemoteUrl} from "../utils/PreviewHelper";
import {IGroup} from "./models";
import {v4} from 'uuid';
import QRCode from "qrcode";
import {rgba2hex} from "../utils/CSSUtils";

const defaultGroupAvatar = 'https://cloudfront.selectdc.net/faf62e78-1916-11e8-909a-0a586460033a.png';

const MainStore = types
    .model({
        currentPage: Screens.welcome,
        previousPage: types.maybeNull(types.union(...Object.values(Screens).map(types.literal))),
        firstScreen: OnboardingStore,
        secondScreen: OnboardingStore,
        landingPage: LandingPageStore,
        auth: AuthStore,
        group: GroupStore,
        deeplink: DeeplinkStore,
    })
    .views(self => ({
        get isStarted() {
            return !!self.group.name
        },
        get imagesAreValid() {
            return self.landingPage.imagesAreValid && self.firstScreen.imagesAreValid && self.secondScreen.imagesAreValid && self.group.imagesAreValid
        },
        get csbPreviewLink() {
            // @ts-ignore
            return `${getCSBURL()}/?preview=${self.base64Data}`
        },
        get landingPagePreviewLink() {
            // @ts-ignore
            return `${getPhoenixURL()}/preview/?preview=${self.base64Data}`
        },
        get base64Data() {
            return Base64.toBase64(JSON.stringify({
                // @ts-ignore
                ...self.deeplinkData,
                creator: self.auth.loggedIn ? self.auth.name : undefined,
                group: {
                    name: self.group.name,
                    avatar: self.group.avatar ? self.group.avatar.url : defaultGroupAvatar,
                },
                user: self.auth.loggedIn && self.deeplink.hasInviter ? {
                    name: self.auth.name,
                    avatar: self.auth.avatar ? self.auth.avatar : '',
                } : undefined
            }), true)
        },
        get allText() {
            let value = "";
            if (self.firstScreen.canProceed) {
                value = value + ` ${self.firstScreen.allText}`
            }
            if (self.secondScreen.canProceed) {
                value = value + ` ${self.secondScreen.allText}`
            }
            return value + ` ${self.landingPage.tagline} ${self.deeplink.ogDescription} ${self.group.name} ${self.group.selectId || ""} `
        },
        get deeplinkData() {
            let intros = [];
            if (self.firstScreen.canProceed) {
                intros.push(self.firstScreen.view)
            }
            if (self.secondScreen.canProceed) {
                intros.push(self.secondScreen.view)
            }
            return {
                backgroundColor: self.landingPage.backgroundColor,
                fontColor: self.landingPage.textColor || 'rgba(0,0,0,1)',
                logo: self.landingPage.logo ? self.landingPage.logo.url : "",
                tagline: self.landingPage.tagline,
                backgroundImage: self.landingPage.backgroundImage ? self.landingPage.backgroundImage.url : "",
                introScreens: intros
            }
        },
        get groups(): Array<IGroup> {

            if (self.deeplink.linkId && self.auth.groups.findIndex((group: IGroup) => group.id === self.group.id) === -1) {
                // Editing link and the group is not part of the groups the user is owner or admin
                const selectedGroup: IGroup = {
                    id: self.group.id,
                    name: self.group.name,
                    avatar: self.group.avatar ? self.group.avatar.url : '',
                    selectId: self.group.selectId,
                    isOwner: self.group.isOwner,
                    allowSendPost: self.group.isMembersCanPostOn,
                    allowAddUser: self.group.isOpen
                };

                return self.auth.groups.concat([selectedGroup]);
            } else {
                return self.auth.groups;
            }

        }
    }))
    .actions(self => ({
        // Navigation
        reUploadAll: function () {
            self.landingPage.reUploadAll();
            self.firstScreen.reUploadAll();
            self.secondScreen.reUploadAll();
            self.group.reUploadAll();
        },
        changeScreen: function (screen: Screens) {
            self.previousPage = self.currentPage;
            self.currentPage = screen;
        },
        logout: function () {
            agent.setToken('');
            self.auth.reset();
            self.group.id = '';
            self.group.extraGroupIds = cast([]);
            self.group.featureGroupIds = cast([]);
            self.deeplink.hasInviter = false;
            self.deeplink.isNewGroup = true;
            if (self.deeplink.linkId) {
                this.forkLink();
            }
        },
        fetchAndSetDefaultCountryCode: flow(function* () {
            try {
                const response = yield fetch('https://ipinfo.io/json?token=b05dd799cbdef4', {cache: 'reload'});
                if (!response.ok) return;
                const {country} = yield response.json();
                const result = CountryCodeList.find(({alpha2Code}) => alpha2Code === country);
                if (!result) return;
                self.auth.defaultCountryCode = cast(result);
                self.auth.countryCode = result.value;
            } catch (e) {
                const result = CountryCodeList.find(({alpha2Code}) => alpha2Code === "US");
                if (!result) return;
                self.auth.defaultCountryCode = cast(result);
                self.auth.countryCode = result.value;
            }
        }),
        forkLink: () => {
            self.deeplink.url = '';
            self.deeplink.linkId = '';
            self.currentPage = Screens.getStarted;
            self.group.isSelectIdAvailable = false;
        },
        checkIsHarmful: flow(function* (): Generator<any, { isHarmful: boolean, words: Array<string> }, any> {
            const {'is_harmful?': isHarmful, words} = yield agent.Invitation.detectHarmfulWords(self.allText);
            return {isHarmful, words}
        }),
        resetLinkStore: () => {
            self.group.reset()
            self.deeplink.reset()
            self.firstScreen.reset()
            self.secondScreen.reset()
            self.landingPage.reset()
        },
        fetchLink: flow(function* (body: { link_id: string } | { select_id: string }, isFork?: boolean): Generator<any, number | null, any> {
            try {
                const data = yield agent.Invitation.fetchLink(body);
                self.currentPage = Screens.selectGroup;

                const mainGroup = data.groups.find((group: any) => group.group_context_id === data.group_context_id);
                const filteredGroups = data.groups.filter((group: any) => group.group_context_id !== data.group_context_id);

                applySnapshot(self.group, {
                    ...GroupDefaultStore,
                    id: data.group_context_id,
                    name: mainGroup.group_name,
                    selectId: mainGroup.select_id,
                    isOwner: data.is_group_owner,
                    fetchingDeeplink: false,
                    avatar: createImageStoreWithRemoteUrl(mainGroup.group_avatar),
                    extraGroupIds: filteredGroups.map((group: { group_context_id: string }) => group.group_context_id)
                });


                applySnapshot(self.deeplink, {
                    ...DeeplinkDefaultStore,
                    ogDescription: data.description,
                    ogDescriptionIsEdited: true,
                    expiration: '',
                    isExpired: !!data.expiration && Date.parse(data.expiration) < Date.now(),
                    isLoading: true,
                    url: data.urls.url,
                    isPrivate: data.is_private,
                    isDefault: data.is_default,
                    email: self.auth.email,
                    isNewGroup: false,
                    hasInviter: data.has_inviter,
                    linkId: data.link_id
                });

                const firstIntroScreen = data.data.introScreens[0];
                if (firstIntroScreen) {
                    applySnapshot(self.firstScreen, {
                        title: firstIntroScreen.title,
                        subtitle: firstIntroScreen.subtitle,
                        color: firstIntroScreen.fontColor,
                        image: createImageStoreWithRemoteUrl(firstIntroScreen.image),
                        backgroundColor: firstIntroScreen.backgroundColor,
                        backgroundImage: createImageStoreWithRemoteUrl(firstIntroScreen.backgroundImage)
                    });
                }


                const secondIntroScreen = data.data.introScreens.length > 1 ? data.data.introScreens[1] : null;
                if (firstIntroScreen) {
                    applySnapshot(self.secondScreen, secondIntroScreen ? {
                        title: secondIntroScreen.title,
                        subtitle: secondIntroScreen.subtitle,
                        color: secondIntroScreen.fontColor,
                        image: createImageStoreWithRemoteUrl(secondIntroScreen.image),
                        backgroundColor: secondIntroScreen.backgroundColor,
                        backgroundImage: createImageStoreWithRemoteUrl(secondIntroScreen.backgroundImage)
                    } : OnboardingDefaultStore);
                }

                applySnapshot(self.landingPage, {
                    tagline: data.data.tagline,
                    logo: createImageStoreWithRemoteUrl(data.data.logo),
                    backgroundColor: data.data.backgroundColor,
                    backgroundImage: createImageStoreWithRemoteUrl(data.data.backgroundImage),
                    textColor: data.data.fontColor
                });

                if (isFork) {
                    self.deeplink.url = '';
                    self.deeplink.linkId = '';
                    self.group.isSelectIdAvailable = false;
                }

                return null;
            } catch (e) {
                return e.status;
            }
        }),
        markDeeplinkAsExpired: flow(function* () {
            try {
                const ogDescription = self.deeplink.ogDescription || formatI18nKeys('link_detail_default_og_description', self.group.name);
                yield agent.Deeplink.editDeeplink(
                    self.deeplink.linkId,
                    self.group.id,
                    self.deeplinkData,
                    !self.deeplink.isPrivate && self.deeplink.isDefault,
                    "-1",
                    ogDescription,
                    self.deeplink.hasInviter,
                    self.deeplink.isPrivate,
                    self.group.extraGroupIds,
                    self.allText,
                    self.auth.email,
                );
                self.deeplink.isExpired = true;
                return true;
            } catch (e) {
                return false;
            }
        }),
        generateDeeplink: flow(function* () {
            self.deeplink.isLoading = true;
            self.deeplink.failedGeneratingLink = false;

            try {

                const ogDescription = self.deeplink.ogDescription || formatI18nKeys('link_detail_default_og_description', self.group.name);

                let data;

                if (self.deeplink.linkId) {
                    // Editing

                    data = yield agent.Deeplink.editDeeplink(
                        self.deeplink.linkId,
                        self.group.id,
                        self.deeplinkData,
                        !self.deeplink.isPrivate && self.deeplink.isDefault,
                        self.deeplink.expiration,
                        ogDescription,
                        self.deeplink.hasInviter,
                        self.deeplink.isPrivate,
                        self.group.extraGroupIds,
                        self.allText,
                        self.auth.email,
                    );
                } else if (!self.deeplink.isNewGroup) {
                    // Creating link with existing group(s)

                    data = yield agent.Deeplink.createDeeplinkAuthenticated(
                        self.group.id,
                        self.deeplinkData,
                        self.auth.email,
                        self.deeplink.expiration,
                        ogDescription,
                        self.deeplink.hasInviter,
                        self.deeplink.isPrivate,
                        self.group.extraGroupIds,
                        !self.deeplink.isPrivate && self.deeplink.isDefault,
                        self.allText
                    );
                } else {
                    // Creating link with a new group

                    data = yield agent.Deeplink.createDeeplinkWithNewGroup(
                        self.group.id,
                        self.group.name,
                        self.group.avatar ? self.group.avatar.url : defaultGroupAvatar,
                        self.deeplinkData,
                        self.auth.email,
                        self.deeplink.expiration,
                        ogDescription,
                        self.deeplink.hasInviter,
                        self.deeplink.isPrivate,
                        self.group.extraGroupIds,
                        !self.deeplink.isPrivate && self.deeplink.isDefault,
                        self.allText,
                        self.group.isOpen,
                        self.group.isMembersCanPostOn,
                        self.group.isGroupHistoryOn,
                        self.group.shareToPublic,
                        self.group.allowCrossExposure,
                        self.group.selectId || undefined
                    );
                }
                // if (self.group.bioGroup) {
                //     yield agent.Group.addGroupBio(self.group.id, self.group.bioGroup);
                // }
                if (self.group.welcomePostText) {
                    yield agent.Group.addWelcomePost(self.group.getWelcomePostPayload,);
                }
                if (self.group.featureGroupIds.length) {
                    yield agent.Group.addFeatureGroups(self.group.id, self.group.featureGroupIds);
                }

                const qrCode = yield QRCode.toDataURL(`${data.url}?utm=qrcode`, {
                    width: 129,
                    color: {
                        light: rgba2hex(self.deeplinkData.backgroundColor),
                        dark: rgba2hex(self.deeplinkData.fontColor)
                    }
                })

                self.deeplink.isExpired = false;
                self.deeplink.url = data.url;
                self.deeplink.urlQrCode = qrCode;
                self.deeplink.textToShare = data.text;
                self.deeplink.isLoading = false;

            } catch (e) {
                if (self.deeplink.isNewGroup) {
                    // Just in case the an error occurred after creating the group on the backend
                    self.group.id = v4();
                }
                self.deeplink.failedGeneratingLink = true;
                self.currentPage = Screens.linkDetails;
            }
        }),
    }));

export interface IMainStore extends Instance<typeof MainStore> {
}

export const defaultStore = {
    auth: AuthDefaultStore,
    firstScreen: OnboardingDefaultStore,
    secondScreen: OnboardingDefaultStore,
    landingPage: LandingPageDefaultStore,
    group: GroupDefaultStore,
    deeplink: DeeplinkDefaultStore,
};

export default MainStore
